import { Localized } from "@fluent/react"
import { Badge, Box, Button, Chip, Divider, IconButton, Menu, MenuItem, Paper, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Typography } from "@mui/material"
import React, { Dispatch, SetStateAction, Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { AppId } from "../../app/appTypes"
import {
    DndContext,
    DragEndEvent,
    DragOverEvent,
    DragOverlay,
    DragStartEvent,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core"
import {
    SortableContext,
    arrayMove,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { DocumentNumberBox } from "../../app/components/DocumentNumberBox"
import { RequirementDescription } from "./RequirementDescription"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { If } from "../../app/If"
import AddIcon from '@mui/icons-material/Add'
import { URSRequirement, Urs, allURSRequirements, allURSRequirementsFlat, createURSRequirement, UrsRequirementType, UrsGroup, addUrsRequirementsFromCatalog, findSuccesor, findPredecessor, reorderRequirements } from "./UrsSlice"
import { REORDER_ACTIVE, REORDER_HIDE, REORDER_HIDE_SLOT, URSRequirementRow } from "./URSRequirementRow"
import { selectAllURSComments, selectURSComments, URSComment } from "./URSCommentSlice"
import { selectAllBlobs } from "../documents/attachementsSlice"
import { store } from "../../app/store"
import { DeleteRequirementDialog } from "./dialogs/DeleteRequirementDialog"
import { EditRequirementDialog } from "./dialogs/EditRequirementDialog"
import { AttachementsDialog } from "./dialogs/AttachementsDialog"
import { CommentsDialog } from "./Comments"
import { RequirementDialogBrowser } from "../requirements/RequirementDialogBrowser"
import { selectRequirementById } from "../requirements/RequirementsDictSlice"
import { CreateMainGroupDialog } from "./dialogs/CreateMainGroupDialog"
import { CreateSubGroupDialog } from "./dialogs/CreateSubGroupDialog"
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { EditMainGroupDialog } from "./dialogs/EditMainGroupDialog"
import { DeleteGroupDialog } from "./dialogs/DeleteGroupDialog"
import { AddToGroupRow, Group } from "./Group"
import { EditGroupDialog } from "./dialogs/EditGroupDialog"
import { canUserComment, canUserEditComment, canUserResolveComment } from "./ursHelper"
import { selectLoggedUser } from "../user/userSlice"
import { AddRequirementDialog } from "./dialogs/AddRequirementDialog"
import { AddChildRequirementDialog } from "./dialogs/AddChildRequirmentDialog"
import { LoadingButton } from "@mui/lab"
import { showError } from "../notifications/notificationsSlice"
import { RequirementFilterBar } from "./components/RequirementFilterBar"
import { runHighlightSearch, clearHighlights } from "../documents/importedDocuments/ast/reviewSearch"
import {
    buildFilterIndex,
    FilterIndex,
    emptyCommentCounts,
    emptyRequirementFilter,
    filteredRequirementsFlat,
    filterRequirementType,
    isFilterActive,
    RequirementFilter,
} from "./requirementFilter"
import { UrsRequirementsAssistant } from "../ai/UrsRequirementsAssistant"

export interface RequirementsPanelProps {
    urs: Urs
    template: boolean
    readOnly?: boolean
    onContextTypeChange?: (id: AppId | null, ctx?: UrsRequirementType) => void
}

export const NoRequirements = () => {
    return <Typography gutterBottom component="div" color="textSecondary" variant="h5">
        <Localized id="urs-no-requirements">Brak wymagań.</Localized>
    </Typography>
}

type DialogState =
    | { tag: 'none' }
    | { tag: 'add', groupId: AppId }
    | {
        tag: 'add-from-catalog'
        , groupId: AppId
        , type: UrsRequirementType
        , selection: Set<AppId>
    }
    | { tag: 'add-child-requirment', parentRequirement: URSRequirement }
    | { tag: 'edit', id: AppId, group: UrsGroup | null, requirement: URSRequirement, rt: UrsRequirementType }
    | { tag: 'copy', srcId: AppId, group: string, typeId: AppId }
    | { tag: 'comments', requirement: URSRequirement }
    | { tag: 'attachements', requirement: URSRequirement }
    | { tag: 'delete', entity: URSRequirement }
    | { tag: 'add-main-group', id: AppId }
    | { tag: 'add-sub-group', id: AppId, groupId: string, groupName: string }
    | { tag: 'edit-main-group', groupId: AppId, groupCode: string, groupName: string }
    | { tag: 'delete-main-group', groupId: AppId }
    | { tag: 'edit-sub-group', groupId: AppId, groupCode: string, groupName: string }
    | { tag: 'delete-sub-group', groupId: AppId }

export const RequirementsPanel = ({ urs, template, readOnly = false, onContextTypeChange }: RequirementsPanelProps) => {
    const { id: ursId } = urs
    const dispatch = useAppDispatch()
    const user = useAppSelector(selectLoggedUser)
    const canUpload = !readOnly && urs.links.save !== undefined
    const { deviceTypeId } = urs
    const [contextTypeId, setContextTypeId] = useState<AppId | null>(() => {
        if (urs.types.length > 0) {
            const [someItem] = urs.types
            return someItem.id
        } else {
            return null
        }
    })
    const [dialog, setDialog] = useState<DialogState>({ tag: 'none' })
    const comments = useAppSelector(state => {

        if (selectURSComments(state).ursId == ursId) {
            return selectAllURSComments(state)
        } else {
            return []
        }
    })

    const [filter, setFilter] = useState<RequirementFilter>(emptyRequirementFilter)
    const filterActive = isFilterActive(filter)
    // Blobs for every requirement are preloaded by EditURS, so this sees the whole
    // picture rather than only the requirements whose dialog has been opened.
    const blobs = useAppSelector(selectAllBlobs)
    const filterIndex = useMemo(() => buildFilterIndex(comments, blobs), [comments, blobs])

    // Reorder state is shared between the tabs (requirement-type order) and the
    // table (requirements / groups / children) so a single Apply/Cancel commits
    // the whole tree via one reorderRequirements dispatch.
    const [localTypes, setLocalTypes] = useState(urs.types)
    useEffect(() => {
        setLocalTypes(urs.types)
    }, [urs])
    const [isDirty, setIsDirty] = useState(false)
    const [saving, setSaving] = useState(false)
    const handleReorderCancel = useCallback(() => {
        setLocalTypes(urs.types)
        setIsDirty(false)
    }, [urs.types])
    const handleReorderSave = useCallback(async () => {
        setSaving(true)
        try {
            await dispatch(reorderRequirements({ id: urs.id, types: localTypes }))
            setIsDirty(false)
        } catch (error) {
            dispatch(showError('error'))
        } finally {
            setSaving(false)
        }
    }, [dispatch, urs.id, localTypes])

    // CALLBACKs

    const setContext = useCallback(
        (id: AppId | null) => {
            setContextTypeId(id)
            if (id === null) {
                onContextTypeChange?.(null, undefined)
            } else {
                const ctx = urs.types.find(x => x.id === id)
                onContextTypeChange?.(id, ctx)
            }
        },
        [onContextTypeChange, urs.types]
    )
    const handleHideDialog = useCallback(() => setDialog({ tag: 'none' }), [])
    const handleRequirementCreation = useCallback((urs: Urs) => {
        if (contextTypeId === null) {
            setContext(urs.types[0]?.id ?? null)
        }
        setDialog({ tag: 'none' })
    }, [contextTypeId])
    const handleOKReqBrowserButtonClick = async (selection: Set<AppId>, sectionGuid: string) => {
        dispatch(addUrsRequirementsFromCatalog({
            ursId,
            sectionGuid,
            catalogIds: selection,
        }))
        if (selection.size > 0 && contextTypeId === null) {
            const someItem = selection.values().next()
            const firstRequirementId = someItem.value
            const someEntity = selectRequirementById(store.getState(), firstRequirementId)
            if (someEntity) {
                setContext(someEntity.typeId)
            }
        }
        handleHideDialog()
    }
    const userCanSendComment = useCallback(() => {
        return !readOnly && canUserComment(urs)
    }, [urs, readOnly])
    const userCanApproveComment = useCallback(() => {
        return !readOnly && canUserResolveComment(urs)
    }, [urs, readOnly])
    const userCanEditComment = useCallback((comment: URSComment) => {
        return !readOnly && canUserEditComment(user, urs, comment)
    }, [urs, user, readOnly])

    // CALLBACKs

    const contextType = localTypes.find(x => x.id === contextTypeId)

    const handleMainGroupAdded: (data: Urs) => void = useCallback(data => {
        if (contextTypeId === null) {
            setContext(data.types[0]?.id)
        }
        setDialog({ tag: 'none' })
    }, [contextTypeId])

    function handleDeleteGroup(data: Urs): void {
        const currentType = data.types.find(x => x.id === contextTypeId)
        if (!currentType) {
            if (data.types.length > 0) {
                setContext(data.types[0].id)
            } else {
                setContext(null)
            }
        }
        setDialog({ tag: 'none' })
    }

    const documentTotal = useMemo(() => Array.from(allURSRequirementsFlat(urs)).length, [urs])
    const filteredFlat = useMemo(
        () => filteredRequirementsFlat(urs, filter, filterIndex),
        [urs, filter, filterIndex]
    )

    // The ids the pager may walk, captured when the dialog opens and deliberately
    // not recomputed while it is open: resolving the last comment on a requirement
    // would otherwise drop it out of an "unresolved" filter mid-review, shrinking
    // the list and renumbering "3 / 12" under the user.
    const [pagerIds, setPagerIds] = useState<AppId[]>([])

    // Read through a ref, not the closure. NonEmptyRequirementsPanel memoises the
    // row callbacks that call this with an empty dep list, so it only ever holds
    // the identity handed to it on the first render — one that closed over the
    // unfiltered document and silently made every snapshot below the whole URS.
    // Keeping this callback stable is what makes those empty dep lists true.
    const filteredFlatRef = useRef(filteredFlat)
    filteredFlatRef.current = filteredFlat

    // Every path that *opens* the dialog goes through here so the snapshot is taken
    // exactly once; the pager itself calls setDialog directly to keep it.
    const handleSetDialog = useCallback((next: DialogState) => {
        if (next.tag === 'edit') {
            setPagerIds(filteredFlatRef.current.map(([r]) => r.id))
        }
        setDialog(next)
    }, [])

    // Pager for the edit dialog. Resolved against the live `urs` rather than the
    // snapshot held in `dialog`, because every save replaces the whole aggregate.
    const editNeighbours = useMemo(() => {
        if (dialog.tag !== 'edit') {
            return { prev: null, next: null, index: 0, total: 0, filtered: false }
        }
        const wanted = new Set(pagerIds)
        // Walking the live document rather than the snapshot keeps document order
        // right, drops requirements deleted since it was taken, and — via the id
        // check — always leaves the open requirement in the list even after it
        // stops matching the filter.
        const items = Array.from(allURSRequirementsFlat(urs))
            .filter(([r]) => wanted.has(r.id) || r.id === dialog.requirement.id)
        const i = items.findIndex(([r]) => r.id === dialog.requirement.id)
        return {
            prev: i > 0 ? items[i - 1] : null,
            next: i !== -1 && i < items.length - 1 ? items[i + 1] : null,
            index: i + 1,
            total: items.length,
            filtered: items.length < documentTotal,
        }
    }, [urs, dialog, pagerIds, documentTotal])

    const matchCountsByType = useMemo(() => {
        const counts = new Map<AppId, number>()
        for (const [, type] of filteredFlat) {
            counts.set(type.id, (counts.get(type.id) ?? 0) + 1)
        }
        return counts
    }, [filteredFlat])

    const handleEditNavigate = useCallback(([requirement, rt]: [URSRequirement, UrsRequirementType]) => {
        const group = rt.groups.find(g => g.requirements.some(r => r.id === requirement.id)) ?? null
        setContext(rt.id) // keep the table behind the dialog on the matching tab
        setDialog({ tag: 'edit', id: requirement.id, group, requirement, rt })
    }, [setContext])

    let panelContent = <></>
    if (contextType === undefined && urs.types.length === 0) {
        panelContent = <Stack direction="row" spacing={4}>
            <NoRequirements />
            <Button
                startIcon={<AddIcon />}
                disabled={readOnly}
                onClick={() => setDialog({ tag: 'add-main-group', id: ursId })}
            >
                <Localized id='urs-add-main-group'>Dodaj grupę główną</Localized>
            </Button>
        </Stack>
    } else if (contextType !== undefined) {
        panelContent = <NonEmptyRequirementsPanel
            setContextTypeId={setContextTypeId}
            requirementType={contextType}
            template={template}
            readOnly={readOnly}
            urs={urs}
            setDialog={handleSetDialog}
            filter={filter}
            filterIndex={filterIndex}
            onClearFilter={() => setFilter(emptyRequirementFilter)}
            localTypes={localTypes}
            setLocalTypes={setLocalTypes}
            isDirty={isDirty}
            setIsDirty={setIsDirty}
            saving={saving}
            handleReorderSave={handleReorderSave}
            handleReorderCancel={handleReorderCancel}
        />
    }

    return <Box sx={{
        display: 'flex',
        height: 'calc(100vh - 340px)',
    }}>
        <If condition={urs.types.length > 0}>
            <RequirementsTabs
                urs={urs}
                typeId={contextTypeId ?? ""}
                setContextTypeId={setContext}
                localTypes={localTypes}
                setLocalTypes={setLocalTypes}
                setIsDirty={setIsDirty}
                editable={canUpload}
                filterActive={filterActive}
                matchCounts={matchCountsByType}
            />
        </If>
        <Stack sx={{
            paddingLeft: 4,
            width: '100%',
            minWidth: 0,
        }} direction="column" spacing={2}>
            <If condition={urs.types.length > 0}>
                <RequirementFilterBar
                    filter={filter}
                    onChange={setFilter}
                    matchCount={filteredFlat.length}
                    totalCount={documentTotal}
                    showSupplierNote={urs.supplierId !== null || urs.internalSupplierId !== null}
                    disabled={isDirty}
                />
            </If>
            {panelContent}
        </Stack>
        {dialog.tag === 'add' && <AddRequirementDialog
            sectionId={dialog.groupId}
            onCancel={handleHideDialog}
            onSuccess={handleRequirementCreation}
        />}
        {dialog.tag === 'add-child-requirment' && <AddChildRequirementDialog
            parentRequirement={dialog.parentRequirement}
            onCancel={handleHideDialog}
            onSuccess={handleRequirementCreation}
        />}
        {dialog.tag === 'edit' && <EditRequirementDialog
            comments={comments}
            requirementGroup={dialog.group}
            requirement={dialog.requirement}
            rootSection={dialog.rt}
            onCancel={handleHideDialog}
            canSendComments={userCanSendComment}
            canResolveComments={userCanApproveComment}
            canEditComment={userCanEditComment}
            isOfferReady={urs.supplierId !== null || urs.internalSupplierId !== null}
            canDelete={canUpload}
            navigation={{
                prev: editNeighbours.prev && (() => handleEditNavigate(editNeighbours.prev!)),
                next: editNeighbours.next && (() => handleEditNavigate(editNeighbours.next!)),
                index: editNeighbours.index,
                total: editNeighbours.total,
                filtered: editNeighbours.filtered,
            }}
        />}
        {dialog.tag === 'delete' && <DeleteRequirementDialog typeId={dialog.entity.typeId ?? ''} group={dialog.entity.group ?? ''} entity={dialog.entity} onSuccess={handleHideDialog} onCancel={handleHideDialog} />}
        {dialog.tag === 'attachements' && <AttachementsDialog
            typeId={dialog.requirement.typeId ?? ''}
            group={dialog.requirement.group ?? ''}
            onClose={handleHideDialog}
            requirementId={dialog.requirement.id}
            requirement={dialog.requirement}
            ursGuid={urs.guid}
            canUpload={canUpload && dialog.requirement.status !== 'DELETED'}
            canDelete={canUpload && dialog.requirement.status !== 'DELETED'}
        />}
        {dialog.tag === 'comments' && <CommentsDialog
            comments={comments}
            ursId={ursId}
            requirement={dialog.requirement}
            onClose={handleHideDialog}
            isCommentEnabled={userCanSendComment}
            isResolvingEnabled={userCanApproveComment}
            isEditEnabled={userCanEditComment}
        />}
        {dialog.tag === 'add-from-catalog' && <RequirementDialogBrowser
            cancel={handleHideDialog}
            performAction={(selection) => handleOKReqBrowserButtonClick(selection, dialog.groupId)}
            deviceTypeId={deviceTypeId}
            disabled={dialog.selection}
            lang={urs.language ?? 'PL'}
            defaultTypeId={dialog.type && dialog.type.sysSectionId ? dialog.type.sysSectionId.toString() : undefined}
        />}
        {dialog.tag === 'add-main-group' && <CreateMainGroupDialog ursId={dialog.id} onCancel={handleHideDialog} onSucces={handleMainGroupAdded} />}
        {dialog.tag === 'add-sub-group' && <CreateSubGroupDialog ursId={dialog.id} parentGroupId={dialog.groupId} parentGroupName={dialog.groupName} onCancel={handleHideDialog} onSucces={handleHideDialog} />}
        {dialog.tag === 'edit-main-group' && <EditMainGroupDialog groupId={dialog.groupId} name={dialog.groupName} code={dialog.groupCode} onCancel={handleHideDialog} onSucces={handleMainGroupAdded} />}
        {dialog.tag === 'edit-sub-group' && <EditGroupDialog groupId={dialog.groupId} name={dialog.groupName} code={dialog.groupCode} onCancel={handleHideDialog} onSucces={handleHideDialog} />}
        {dialog.tag === 'delete-main-group' && <DeleteGroupDialog id={dialog.groupId} onCancel={handleHideDialog} onSuccess={handleDeleteGroup} />}
    </Box>
}

// A visible slice of the tree together with its ids. The ids are kept alongside the
// items, rather than mapped at the point of use, so SortableContext receives the
// same array instance on every render it does not need to change for.
type VisibleList<T> = { items: T[]; ids: AppId[] }
const emptyVisibleList: VisibleList<URSRequirement> = { items: [], ids: [] }

// Requirement bodies only. Anything outside this — numbering, status chips, the
// supplier comment column — is deliberately not searched, so the highlight can
// never disagree with what the filter matched on.
const REQ_CONTENT_SELECTOR = '[data-req-content]'

type ItemKind = 'root' | 'group' | 'child'
type ItemLocation = { kind: ItemKind; typeId: AppId; containerId: AppId; index: number }
type ContainerInfo = { kind: ItemKind; typeId: AppId; containerId: AppId }

function findItemLocation(types: UrsRequirementType[], itemId: AppId): ItemLocation | null {
    for (const type of types) {
        const idx = type.requirements.findIndex(r => r.id === itemId)
        if (idx !== -1) return { kind: 'root', typeId: type.id, containerId: type.id, index: idx }
        for (const req of type.requirements) {
            const cIdx = req.childRequirements.findIndex(c => c.id === itemId)
            if (cIdx !== -1) return { kind: 'child', typeId: type.id, containerId: req.id, index: cIdx }
        }
        for (const group of type.groups) {
            const gIdx = group.requirements.findIndex(r => r.id === itemId)
            if (gIdx !== -1) return { kind: 'group', typeId: type.id, containerId: group.id, index: gIdx }
            for (const req of group.requirements) {
                const cIdx = req.childRequirements.findIndex(c => c.id === itemId)
                if (cIdx !== -1) return { kind: 'child', typeId: type.id, containerId: req.id, index: cIdx }
            }
        }
    }
    return null
}

// Only real containers (types and groups) resolve here. Requirement ids (root,
// group, or child) intentionally fall through so resolveContainer defers to
// findItemLocation, which maps them to their actual parent container. Claiming
// requirement ids here would break requirement-to-requirement reordering.
function findContainer(types: UrsRequirementType[], id: AppId): ContainerInfo | null {
    for (const type of types) {
        if (type.id === id) return { kind: 'root', typeId: type.id, containerId: type.id }
        for (const group of type.groups) {
            if (group.id === id) return { kind: 'group', typeId: type.id, containerId: group.id }
        }
    }
    return null
}

function resolveContainer(types: UrsRequirementType[], id: AppId): ContainerInfo | null {
    const direct = findContainer(types, id)
    if (direct) return direct
    const item = findItemLocation(types, id)
    if (item) return { kind: item.kind, typeId: item.typeId, containerId: item.containerId }
    return null
}

function moveItemBetweenContainers(
    types: UrsRequirementType[],
    itemId: AppId,
    targetContainerId: AppId,
    targetIndex: number
): UrsRequirementType[] | null {
    const src = findItemLocation(types, itemId)
    if (!src) return null
    if (src.kind === 'child') return null // children cannot change parent
    const tgt = findContainer(types, targetContainerId)
    if (!tgt) return null
    if (src.typeId !== tgt.typeId) return null

    const sameContainer = src.containerId === targetContainerId
    const insertIndex = sameContainer && targetIndex > src.index ? targetIndex - 1 : targetIndex

    return types.map(type => {
        if (type.id !== src.typeId) return type

        const srcList = src.kind === 'root'
            ? type.requirements
            : type.groups.find(g => g.id === src.containerId)!.requirements
        const movedReq = { ...srcList[src.index], group: tgt.kind === 'root' ? null : tgt.containerId }

        let rootReqs = type.requirements
        let groups = type.groups

        if (src.kind === 'root') {
            rootReqs = rootReqs.filter((_, i) => i !== src.index)
        } else {
            groups = groups.map(g =>
                g.id !== src.containerId ? g
                    : { ...g, requirements: g.requirements.filter((_, i) => i !== src.index) }
            )
        }

        if (tgt.kind === 'root') {
            rootReqs = [...rootReqs.slice(0, insertIndex), movedReq, ...rootReqs.slice(insertIndex)]
        } else {
            groups = groups.map(g => {
                if (g.id !== tgt.containerId) return g
                const list = g.requirements
                return { ...g, requirements: [...list.slice(0, insertIndex), movedReq, ...list.slice(insertIndex)] }
            })
        }

        return { ...type, requirements: rootReqs, groups }
    })
}

interface NonEmptyRequirementsPanelProps extends RequirementsPanelProps {
    setDialog: (dialog: DialogState) => void
    requirementType: UrsRequirementType
    setContextTypeId: Dispatch<SetStateAction<AppId | null>>
    localTypes: UrsRequirementType[]
    setLocalTypes: Dispatch<SetStateAction<UrsRequirementType[]>>
    isDirty: boolean
    setIsDirty: Dispatch<SetStateAction<boolean>>
    saving: boolean
    handleReorderSave: () => void
    handleReorderCancel: () => void
    filter: RequirementFilter
    filterIndex: FilterIndex
    onClearFilter: () => void
}
const NonEmptyRequirementsPanel = ({ urs, setDialog, template, readOnly = false, requirementType, setContextTypeId, localTypes, setLocalTypes, isDirty, setIsDirty, saving, handleReorderSave, handleReorderCancel, filter, filterIndex, onClearFilter }: NonEmptyRequirementsPanelProps) => {
    const { id: ursId } = urs

    const assistantDocumentId = useMemo(() => {
        const normalized = ursId.trim()
        if (!/^\d+$/.test(normalized)) {
            return null
        }
        const parsed = Number(normalized)
        return Number.isSafeInteger(parsed) ? parsed : null
    }, [ursId])

    const dispatch = useAppDispatch()

    const tableRef = useRef<null | HTMLDivElement>(null)

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const groupMenuOpen = Boolean(anchorEl);

    const showSupplierComment = urs.supplierId != null || urs.internalSupplierId != null
    const editable = !readOnly && urs.links.save !== undefined

    const [activeReq, setActiveReq] = useState<URSRequirement | null>(null)
    const [activeGroup, setActiveGroup] = useState<UrsGroup | null>(null)
    const dragSnapshotRef = useRef<UrsRequirementType[] | null>(null)

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

    const localRequirementType = localTypes.find(t => t.id === requirementType.id) ?? requirementType
    const addChildRequirementActive = !readOnly && urs.links.addChildRequirement !== undefined

    const filterActive = isFilterActive(filter)
    const filterResult = useMemo(
        () => filterRequirementType(localRequirementType, filter, filterIndex),
        [localRequirementType, filter, filterIndex]
    )
    // A drop writes indices computed from the unfiltered tree, so dragging a
    // partially rendered list would silently reorder the wrong requirements.
    const canReorder = editable && !filterActive
    // A deleted requirement has no position worth choosing — it is on screen to be
    // read, not placed. Note this only removes the handle: it does not stop a reorder
    // of the *other* rows from renumbering deleted ones, because they travel in the
    // same payload. That has to be handled server-side (UrsStructureReorderService).
    const rowCanReorder = useCallback(
        (req: URSRequirement) => canReorder && req.status !== 'DELETED',
        [canReorder]
    )
    // Shown only to keep a matching child under its own parent — not itself a match.
    const isContextOnly = useCallback(
        (req: URSRequirement) => filterActive && !filterResult.matched.has(req.id),
        [filterActive, filterResult]
    )
    // The badge counts the rows used to scan the whole comment list for, twice each.
    // Note this index is built from the comments guarded by ursId (see the parent),
    // so a slice still holding another URS's comments now yields 0 rather than that
    // document's counts.
    const commentCountsFor = useCallback(
        (req: URSRequirement) => filterIndex.comments.get(req.id) ?? emptyCommentCounts,
        [filterIndex]
    )

    // Built once per real change rather than per render, because the id arrays are
    // handed to SortableContext, which memoises on `items` *by identity*. A fresh
    // array each render invalidated its context value, so every useSortable below
    // re-rendered and every getSortedRects re-ran — roughly 240 rows and 242
    // contexts on a full tab — whenever anything above re-rendered this panel.
    const visible = useMemo(() => {
        const listOf = (reqs: URSRequirement[]): VisibleList<URSRequirement> => {
            const items = reqs.filter(r => !filterActive || filterResult.visible.has(r.id))
            return { items, ids: items.map(r => r.id) }
        }
        const childrenOf = new Map<AppId, VisibleList<URSRequirement>>()
        const collectChildren = (reqs: URSRequirement[]) => {
            for (const r of reqs) childrenOf.set(r.id, listOf(r.childRequirements))
        }

        const roots = listOf(localRequirementType.requirements)
        collectChildren(roots.items)

        const groupItems = localRequirementType.groups.filter(
            g => !filterActive || filterResult.visibleGroups.has(g.id)
        )
        const groups: VisibleList<UrsGroup> = { items: groupItems, ids: groupItems.map(g => g.id) }
        const requirementsOfGroup = new Map<AppId, VisibleList<URSRequirement>>()
        // The row's `group` prop used to be built inline, so it was a new object on
        // every render — the one prop that would have defeated the memo on the row.
        const groupPropOf = new Map<AppId, UrsGroup & { typeDescription: string }>()
        const typeDescription = `${requirementType.code} ${requirementType.name}`
        for (const g of groupItems) {
            const list = listOf(g.requirements)
            requirementsOfGroup.set(g.id, list)
            groupPropOf.set(g.id, { ...g, typeDescription })
            collectChildren(list.items)
        }

        // Every sortable id in the table, in DOM order. dnd-kit re-measures only the
        // ids of the SortableContext the resized row belongs to, but a row that swaps
        // its "Loading data…" mask for real content shifts every row *below* it —
        // across context boundaries, since children, groups and group requirements
        // each get their own SortableContext. Handing this list to every row's
        // useSortable makes one resize re-measure the whole table instead. Built here
        // so it inherits this memo's identity and does not defeat the row memo.
        const allIds: AppId[] = []
        const pushWithChildren = (reqs: URSRequirement[]) => {
            for (const r of reqs) {
                allIds.push(r.id)
                const kids = childrenOf.get(r.id)
                if (kids) for (const id of kids.ids) allIds.push(id)
            }
        }
        pushWithChildren(roots.items)
        for (const g of groupItems) {
            allIds.push(g.id)
            pushWithChildren(requirementsOfGroup.get(g.id)?.items ?? [])
        }

        return { roots, groups, requirementsOfGroup, childrenOf, groupPropOf, allIds }
    }, [localRequirementType, filterActive, filterResult, requirementType])

    // `visible` is rebuilt on every setLocalTypes, so allIds is a fresh array after
    // every drop — and it is a prop on a memoised row, so that alone re-rendered all
    // ~200 rows. Order is irrelevant to updateMeasurementsFor and a reorder never
    // changes the id *set*, so hold the previous instance until a requirement is
    // actually added or removed.
    const measureIdsRef = useRef<AppId[]>(visible.allIds)
    const measureIds = useMemo(() => {
        const prev = measureIdsRef.current
        const next = visible.allIds
        if (prev !== next && prev.length === next.length) {
            const prevSet = new Set(prev)
            if (next.every(id => prevSet.has(id))) return prev
        }
        measureIdsRef.current = next
        return next
    }, [visible])

    const visibleRootRequirements = visible.roots.items
    const visibleGroups = visible.groups.items
    const nothingMatches = filterActive && filterResult.visible.size === 0

    useLayoutEffect(() => {
        if (tableRef.current) {
            tableRef.current.scroll({ top: 0, behavior: 'auto' })
        }
    }, [requirementType.id])

    const searchText = filter.text.trim()

    // Painting runs against the live DOM, so it has to happen after render and again
    // whenever the rows change underneath it — rows below the fold hold a placeholder
    // until their IntersectionObserver fires, and "Show more" swaps in the rest of a
    // body. A MutationObserver catches all of those without a scroll listener.
    //
    // Attribute mutations are deliberately left unobserved: runHighlightSearch toggles
    // a `search-result` class on every block it matches, which would retrigger this
    // immediately and loop.
    useEffect(() => {
        const root = tableRef.current
        if (root === null) return

        let timer: ReturnType<typeof setTimeout> | null = null
        const paint = () => { runHighlightSearch(root, searchText, REQ_CONTENT_SELECTOR) }
        const schedule = () => {
            if (timer !== null) clearTimeout(timer)
            timer = setTimeout(paint, 80)
        }

        paint()
        const observer = new MutationObserver(schedule)
        observer.observe(root, { childList: true, subtree: true, characterData: true })

        return () => {
            observer.disconnect()
            if (timer !== null) clearTimeout(timer)
            clearHighlights()
        }
    }, [searchText, visible])

    const handleDndDragStart = (e: DragStartEvent) => {
        dragSnapshotRef.current = localTypes
        if (e.active.data.current?.type === 'group') {
            const group = localRequirementType.groups.find(g => g.id === e.active.id)
            if (group) setActiveGroup(group)
            return
        }
        const loc = findItemLocation(localTypes, e.active.id as AppId)
        if (!loc) return
        const type = localTypes.find(t => t.id === loc.typeId)
        if (!type) return
        let req: URSRequirement | undefined
        if (loc.kind === 'root') {
            req = type.requirements[loc.index]
        } else if (loc.kind === 'group') {
            req = type.groups.find(g => g.id === loc.containerId)?.requirements[loc.index]
        } else {
            const parentReq = [...type.requirements, ...type.groups.flatMap(g => g.requirements)]
                .find(r => r.id === loc.containerId)
            req = parentReq?.childRequirements[loc.index]
        }
        if (!req) return
        setActiveReq(req)
    }

    const handleDndDragOver = (e: DragOverEvent) => {
        const { active, over } = e
        if (!over) return
        if (active.id === over.id) return
        if (active.data.current?.type === 'group') return

        const activeLoc = findItemLocation(localTypes, active.id as AppId)
        if (!activeLoc) return

        const overContainer = resolveContainer(localTypes, over.id as AppId)
        if (!overContainer) return
        if (activeLoc.containerId === overContainer.containerId) return

        let targetIndex: number
        if (over.id === overContainer.containerId) {
            targetIndex = 0
        } else {
            const overItem = findItemLocation(localTypes, over.id as AppId)
            if (!overItem) return
            targetIndex = overItem.index
        }

        const next = moveItemBetweenContainers(localTypes, active.id as AppId, overContainer.containerId, targetIndex)
        if (next) setLocalTypes(next)
    }

    const handleDndDragEnd = (e: DragEndEvent) => {
        const { active, over } = e
        const snapshot = dragSnapshotRef.current
        dragSnapshotRef.current = null

        if (active.data.current?.type === 'group') {
            setActiveGroup(null)
            if (!over || active.id === over.id) return
            const srcIdx = localRequirementType.groups.findIndex(g => g.id === active.id)
            const tgtIdx = localRequirementType.groups.findIndex(g => g.id === over.id)
            if (srcIdx === -1 || tgtIdx === -1 || srcIdx === tgtIdx) return
            setLocalTypes(localTypes.map(type =>
                type.id !== localRequirementType.id ? type
                    : { ...type, groups: arrayMove(type.groups, srcIdx, tgtIdx) }
            ))
            setIsDirty(true)
            return
        }

        setActiveReq(null)
        if (!over) return

        const currentLoc = findItemLocation(localTypes, active.id as AppId)
        if (!currentLoc) return

        let finalIndex = currentLoc.index
        const overContainer = resolveContainer(localTypes, over.id as AppId)
        if (overContainer && currentLoc.containerId === overContainer.containerId && active.id !== over.id) {
            const overItem = findItemLocation(localTypes, over.id as AppId)
            const targetIndex = overItem
                ? overItem.index
                : over.id === overContainer.containerId ? 0 : null
            if (targetIndex !== null && targetIndex !== currentLoc.index) {
                setLocalTypes(localTypes.map(type => {
                    if (type.id !== currentLoc.typeId) return type
                    if (currentLoc.kind === 'root') {
                        return { ...type, requirements: arrayMove(type.requirements, currentLoc.index, targetIndex) }
                    }
                    if (currentLoc.kind === 'group') {
                        return {
                            ...type,
                            groups: type.groups.map(g =>
                                g.id !== currentLoc.containerId ? g
                                    : { ...g, requirements: arrayMove(g.requirements, currentLoc.index, targetIndex) }
                            ),
                        }
                    }
                    // child requirement: containerId is the parent requirement's id
                    return {
                        ...type,
                        requirements: type.requirements.map(req =>
                            req.id !== currentLoc.containerId ? req
                                : { ...req, childRequirements: arrayMove(req.childRequirements, currentLoc.index, targetIndex) }
                        ),
                        groups: type.groups.map(g => ({
                            ...g,
                            requirements: g.requirements.map(req =>
                                req.id !== currentLoc.containerId ? req
                                    : { ...req, childRequirements: arrayMove(req.childRequirements, currentLoc.index, targetIndex) }
                            ),
                        })),
                    }
                }))
                finalIndex = targetIndex
            }
        }

        const originalLoc = snapshot ? findItemLocation(snapshot, active.id as AppId) : null
        const positionUnchanged = originalLoc !== null
            && originalLoc.containerId === currentLoc.containerId
            && originalLoc.index === finalIndex

        if (positionUnchanged) {
            if (snapshot) setLocalTypes(snapshot)
        } else {
            setIsDirty(true)
        }
    }

    const handleDndDragCancel = () => {
        setActiveReq(null)
        setActiveGroup(null)
        if (dragSnapshotRef.current) {
            setLocalTypes(dragSnapshotRef.current)
        }
        dragSnapshotRef.current = null
    }

    // CALLBACKs
    // `setDialog` belongs in every dep list below even though it never changes:
    // the parent's handleSetDialog snapshots the pager's id list, so a callback
    // that holds an older one opens the dialog against a stale filter.
    const handleAddChildRequirmentClick = useCallback((parentRequirement: URSRequirement) =>
        setDialog({ tag: 'add-child-requirment', parentRequirement }), [setDialog])

    const handleEditRequirementClick = useCallback((id: AppId, group: UrsGroup | null, requirement: URSRequirement, rt: UrsRequirementType) =>
        setDialog({ tag: 'edit', id, group, requirement, rt }), [setDialog])
    const handleCopyRequirementClick = useCallback(async (id: AppId, group: UrsGroup | null, requirement: URSRequirement, rt: UrsRequirementType) => {
        let src: URSRequirement | undefined
        for (const req of allURSRequirements(urs)) {
            if (req.id === id) {
                src = req
                break
            }
        }
        if (src) {
            const newUrs = await dispatch(createURSRequirement({ ...src, id: '' })).unwrap()
            setDialog({ tag: 'edit', id: newUrs.id, group, requirement, rt })
        }
    }, [urs, dispatch, setDialog])
    const handleDeleteRequirementClick = useCallback((entity: URSRequirement) =>
        setDialog({ tag: 'delete', entity }), [setDialog])
    const handleCommentsClick = useCallback((requirement: URSRequirement) =>
        setDialog({ tag: 'comments', requirement }), [setDialog])
    const handleAttachementsClick = useCallback((requirement: URSRequirement) =>
        setDialog({ tag: 'attachements', requirement }), [setDialog])
    const handleAddReqFromBrowserButtonClick = useCallback((groupId: AppId, rt: UrsRequirementType) => {
        const allCopied: Set<AppId> = new Set()
        for (const r of allURSRequirements(urs)) {
            if (r.requirementId !== null) {
                allCopied.add(r.requirementId.toString())
            }
        }
        setDialog({ tag: 'add-from-catalog', groupId, type: rt, selection: allCopied })
    }, [urs, setDialog])
    const handleAddFromCatalogWithoutGroup = useCallback(() => {
        const allCopied: Set<AppId> = new Set()
        for (const r of allURSRequirements(urs)) {
            if (r.requirementId !== null) {
                allCopied.add(r.requirementId.toString())
            }
        }
        setDialog({ tag: 'add-from-catalog', groupId: requirementType.id, type: requirementType, selection: allCopied })
        setAnchorEl(null)
    }, [requirementType])
    // CALLBACKs

    const mask = useMemo(() => {
        if (showSupplierComment) {
            return <>
                <TableCell></TableCell>
                <TableCell>Loading data...</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
            </>
        } else {
            return <>
                <TableCell></TableCell>
                <TableCell>Loading data...</TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
            </>
        }
    }, [showSupplierComment])

    return <>
        <Typography variant="subtitle2">
            <Stack direction="row" justifyContent="space-between">
                <Stack direction='row' alignItems='center' spacing={2}>
                    <span>{`${requirementType.numeration}. ${requirementType.code} ${requirementType.name}`}</span>
                    {/* The two adds users reach for constantly are lifted out of the
                        overflow menu. Disabled — not hidden — while a reorder is
                        pending, so the header keeps its shape and the warning chip
                        beside them explains the state. */}
                    {editable && <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Button
                            size="small"
                            startIcon={<AddIcon />}
                            disabled={isDirty}
                            onClick={() => setDialog({ tag: 'add-sub-group', id: ursId, groupId: requirementType.id, groupName: requirementType.name })}
                        >
                            <Localized id='urs-add-sub-group'>Dodaj grupę</Localized>
                        </Button>
                        {/* Root-level requirements are only legal while the section has
                            no groups; once it does, every group row carries its own Add. */}
                        <If condition={requirementType.groups.length === 0}>
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                disabled={isDirty}
                                onClick={() => setDialog({ tag: 'add', groupId: requirementType.id })}
                            >
                                <Localized id='urs-add-requirement'>Dodaj wymaganie</Localized>
                            </Button>
                        </If>
                        {assistantDocumentId !== null && <UrsRequirementsAssistant
                            documentId={assistantDocumentId}
                            sectionId={requirementType.id}
                            sectionName={`${requirementType.code} ${requirementType.name}`.trim()}
                            structureId={urs.deviceTypeId}
                        />} 
                    </Stack>}
                    {editable && <IconButton sx={{ height: 40 }} onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)}>
                        <MoreVertIcon />
                    </IconButton>}
                    <Menu anchorEl={anchorEl} open={groupMenuOpen} onClose={() => setAnchorEl(null)}>
                        <MenuItem key='edit' onClick={() => {
                            setDialog({
                                tag: 'edit-main-group',
                                groupId: requirementType.id,
                                groupCode: requirementType.code,
                                groupName: requirementType.name
                            })
                            setAnchorEl(null)
                        }}><Localized id='edit'>Edytuj</Localized></MenuItem>
                        <MenuItem key='delete' disabled={requirementType.groups.length > 0} onClick={() => {
                            setDialog({ tag: 'delete-main-group', groupId: requirementType.id })
                            setAnchorEl(null)
                        }}><Localized id='delete'>Usuń</Localized></MenuItem>
                        <Divider />
                        {/* Anything that creates content re-fetches the URS, and the
                            [urs] effect then overwrites localTypes — an unsaved reorder
                            would disappear while its warning chip still claimed it was
                            pending. The drag gesture itself blocks the menu; this
                            covers the far longer wait between drop and Apply/Cancel. */}
                        <MenuItem key='add-main-group' disabled={isDirty} onClick={() => {
                            setDialog({ tag: 'add-main-group', id: ursId })
                            setAnchorEl(null)
                        }}>
                            <Localized id='urs-add-main-group'>Dodaj grupę główną</Localized>
                        </MenuItem>
                        <MenuItem disabled={isDirty || requirementType.groups.length > 0} key='add-from-catalog' onClick={handleAddFromCatalogWithoutGroup}>
                            <Localized id='urs-add-from-catalog'>Katalog</Localized>
                        </MenuItem>
                    </Menu>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        {isDirty && <>
                            <Chip
                                label={<Localized id="urs-reorder-mode-hint">Reordering mode — unsaved changes</Localized>}
                                color="warning"
                                size="small"
                            />
                            <LoadingButton loading={saving} size="small" onClick={handleReorderCancel}>
                                <Localized id="urs-reorder-cancel">Cancel</Localized>
                            </LoadingButton>
                            <LoadingButton loading={saving} onClick={handleReorderSave} size="small" variant="contained" color="primary">
                                <Localized id="urs-reorder-apply">Apply changes</Localized>
                            </LoadingButton>
                        </>}
                    </Stack>
                </Stack>
            </Stack>
        </Typography>
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDndDragStart}
            onDragOver={handleDndDragOver}
            onDragEnd={handleDndDragEnd}
            onDragCancel={handleDndDragCancel}
        >
            <TableContainer ref={tableRef} className={isDirty ? REORDER_ACTIVE : undefined} sx={{
                maxHeight: 'calc(100vh - 380px)',
                // registerHighlight only records the ranges; without a ::highlight()
                // rule they paint nothing at all.
                '& ::highlight(search-result)': {
                    bgcolor: 'var(--yellow-400)',
                    textDecoration: 'underline',
                },
                // Row actions are unavailable while a reorder is pending. Hiding them
                // from here, off one class, rather than from a `reorderMode` prop on
                // every row: the prop put reorderMode in the row body's memo deps, so
                // the first drop and every Cancel re-rendered all ~200 row bodies and
                // mounted/unmounted their buttons — ~1800 DOM nodes, ~776 ms at 200
                // rows, before the browser's own layout and paint.
                [`&.${REORDER_ACTIVE} .${REORDER_HIDE}`]: { display: 'none' },
                [`&.${REORDER_ACTIVE} .${REORDER_HIDE_SLOT}`]: { visibility: 'hidden' },
            }} component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 32, maxWidth: 32, padding: '0 4px' }} />
                            <TableCell sx={{ minWidth: 50, width: 80, maxWidth: 80 }}>
                                <Typography component="span" variant="subtitle2" color="textSecondary">
                                    <Localized id="urs-item-lp">Lp</Localized>
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography component="span" variant="subtitle2" color="textSecondary">
                                    <Localized id="urs-item-name">Nazwa</Localized>
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ minWidth: 120, width: 120, maxWidth: 120 }}>
                                <Typography component="span" variant="subtitle2" color="textSecondary">
                                    <Localized id="urs-item-criticality">Krytyczność</Localized>
                                </Typography>
                            </TableCell>
                            <If condition={showSupplierComment}>
                                <TableCell sx={{ minWidth: 120, width: 120, maxWidth: 120 }}>
                                    <Typography component="span" variant="subtitle2" color="textSecondary">
                                        <Localized id="urs-item-note">Ocena</Localized>
                                    </Typography>
                                </TableCell>
                                <TableCell >
                                    <Typography component="span" variant="subtitle2" color="textSecondary">
                                        <Localized id="urs-item-comment">Komentarz</Localized>
                                    </Typography>
                                </TableCell>
                            </If>
                            <TableCell sx={{ minWidth: 80, width: 80, maxWidth: 80, justifyContent: 'flex-end' }}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <If condition={nothingMatches}>
                            <TableRow>
                                <TableCell colSpan={showSupplierComment ? 7 : 5} sx={{ textAlign: 'center', py: 4 }}>
                                    <Stack direction="column" spacing={1} alignItems="center">
                                        <Typography variant="body2" color="text.secondary">
                                            <Localized id="urs-filter-no-matches">
                                                No requirements in this section match the filter.
                                            </Localized>
                                        </Typography>
                                        <Button size="small" onClick={onClearFilter}>
                                            <Localized id="clear">Clear</Localized>
                                        </Button>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        </If>
                        <SortableContext
                            items={visible.roots.ids}
                            strategy={verticalListSortingStrategy}
                        >
                            {visibleRootRequirements.map(requirement => {
                                const children = visible.childrenOf.get(requirement.id) ?? emptyVisibleList
                                return <Fragment key={requirement.id}>
                                    <URSRequirementRow
                                        id={requirement.id}
                                        onAddChildRequirmentClick={handleAddChildRequirmentClick}
                                        onEditClick={handleEditRequirementClick}
                                        onCopyClick={handleCopyRequirementClick}
                                        onDeleteClick={handleDeleteRequirementClick}
                                        canEdit={editable}
                                        canReorder={rowCanReorder(requirement)}
                                        contextOnly={isContextOnly(requirement)}
                                        canAddChildRequirement={addChildRequirementActive}
                                        canDelete={editable}
                                        showSupplierComment={showSupplierComment}
                                        onCommentsClick={handleCommentsClick}
                                        onAttachementsClick={handleAttachementsClick}
                                        template={template}
                                        readOnly={readOnly}
                                        requirement={requirement}
                                        commentCounts={commentCountsFor(requirement)}
                                        searchText={searchText}
                                        group={null}
                                        rt={requirementType}
                                        tableRef={tableRef}
                                        mask={mask}
                                        measureIds={measureIds}
                                    />
                                    <SortableContext
                                        items={children.ids}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {children.items.map(childReq =>
                                            <URSRequirementRow
                                                key={childReq.id}
                                                id={childReq.id}
                                                onAddChildRequirmentClick={handleAddChildRequirmentClick}
                                                onEditClick={handleEditRequirementClick}
                                                onCopyClick={handleCopyRequirementClick}
                                                onDeleteClick={handleDeleteRequirementClick}
                                                canEdit={editable}
                                                canReorder={rowCanReorder(childReq)}
                                                contextOnly={isContextOnly(childReq)}
                                                canAddChildRequirement={addChildRequirementActive}
                                                canDelete={editable}
                                                showSupplierComment={showSupplierComment}
                                                onCommentsClick={handleCommentsClick}
                                                onAttachementsClick={handleAttachementsClick}
                                                template={template}
                                                readOnly={readOnly}
                                                requirement={childReq}
                                                commentCounts={commentCountsFor(childReq)}
                                                searchText={searchText}
                                                group={null}
                                                tableRef={tableRef}
                                                mask={mask}
                                                measureIds={measureIds}
                                                rt={requirementType}
                                            />
                                        )}
                                    </SortableContext>
                                </Fragment>
                            })}
                        </SortableContext>
                        {/* Root-level requirements and groups are mutually exclusive in a
                            section — the header menu's "add requirement" is disabled the
                            moment a group exists, so this offer follows the same rule. */}
                        <If condition={editable && !isDirty && requirementType.groups.length === 0}>
                            <AddToGroupRow
                                groupId={requirementType.id}
                                groupName={requirementType.name}
                                onAdd={groupId => setDialog({ tag: 'add', groupId })}
                                showSupplierComments={showSupplierComment}
                            />
                        </If>
                        <SortableContext
                            items={visible.groups.ids}
                            strategy={verticalListSortingStrategy}
                        >
                            {visibleGroups.map(group => {
                                const groupRequirements = visible.requirementsOfGroup.get(group.id) ?? emptyVisibleList
                                return <Fragment key={group.id}>
                                    <Group
                                        group={group}
                                        handleAddRequirement={(groupId) => setDialog({ tag: 'add', groupId })}
                                        handleAddFromCatalog={() => handleAddReqFromBrowserButtonClick(group.id, requirementType)}
                                        handleEditGroup={group => { setDialog({ tag: 'edit-sub-group', groupId: group.id, groupName: group.name, groupCode: '' }) }}
                                        handleDeleteGroup={group => { setDialog({ tag: 'delete-main-group', groupId: group.id }) }}
                                        hasAnyRequirement={group.requirements.length > 0}
                                        canEdit={editable && !isDirty}
                                        canReorder={canReorder}
                                        showSupplierComments={showSupplierComment}
                                    />
                                    <SortableContext
                                        items={groupRequirements.ids}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {groupRequirements.items.map(requirement => {
                                            const children = visible.childrenOf.get(requirement.id) ?? emptyVisibleList
                                            return <Fragment key={requirement.id}>
                                                <URSRequirementRow
                                                    id={requirement.id}
                                                    onAddChildRequirmentClick={handleAddChildRequirmentClick}
                                                    onEditClick={handleEditRequirementClick}
                                                    onCopyClick={handleCopyRequirementClick}
                                                    onDeleteClick={handleDeleteRequirementClick}
                                                    canEdit={editable}
                                                    canReorder={rowCanReorder(requirement)}
                                                    contextOnly={isContextOnly(requirement)}
                                                    canDelete={editable}
                                                    canAddChildRequirement={addChildRequirementActive}
                                                    showSupplierComment={showSupplierComment}
                                                    onCommentsClick={handleCommentsClick}
                                                    onAttachementsClick={handleAttachementsClick}
                                                    template={template}
                                                    readOnly={readOnly}
                                                    requirement={requirement}
                                                    commentCounts={commentCountsFor(requirement)}
                                                    searchText={searchText}
                                                    group={visible.groupPropOf.get(group.id) ?? null}
                                                    rt={requirementType}
                                                    tableRef={tableRef}
                                                    mask={mask}
                                                    measureIds={measureIds}
                                                />
                                                <SortableContext
                                                    items={children.ids}
                                                    strategy={verticalListSortingStrategy}
                                                >
                                                    {children.items.map(childReq =>
                                                        <URSRequirementRow
                                                            key={childReq.id}
                                                            id={childReq.id}
                                                            onAddChildRequirmentClick={handleAddChildRequirmentClick}
                                                            onEditClick={handleEditRequirementClick}
                                                            onCopyClick={handleCopyRequirementClick}
                                                            onDeleteClick={handleDeleteRequirementClick}
                                                            canEdit={editable}
                                                            canReorder={rowCanReorder(childReq)}
                                                            contextOnly={isContextOnly(childReq)}
                                                            canAddChildRequirement={addChildRequirementActive}
                                                            canDelete={editable}
                                                            showSupplierComment={showSupplierComment}
                                                            onCommentsClick={handleCommentsClick}
                                                            onAttachementsClick={handleAttachementsClick}
                                                            template={template}
                                                            readOnly={readOnly}
                                                            requirement={childReq}
                                                            commentCounts={commentCountsFor(childReq)}
                                                            searchText={searchText}
                                                            group={visible.groupPropOf.get(group.id) ?? null}
                                                            rt={requirementType}
                                                            tableRef={tableRef}
                                                            mask={mask}
                                                            measureIds={measureIds}
                                                        />
                                                    )}
                                                </SortableContext>
                                            </Fragment>
                                        })}
                                    </SortableContext>
                                    <If condition={editable && !isDirty}>
                                        <AddToGroupRow
                                            groupId={group.id}
                                            groupName={group.name}
                                            onAdd={groupId => setDialog({ tag: 'add', groupId })}
                                            showSupplierComments={showSupplierComment}
                                        />
                                    </If>
                                </Fragment>
                            })}
                        </SortableContext>
                    </TableBody>
                </Table>
            </TableContainer>
            <DragOverlay style={{ pointerEvents: 'none' }}>
                {activeReq ? (
                    <Paper
                        elevation={6}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            padding: 1,
                            opacity: 0.95,
                            cursor: 'grabbing',
                            maxWidth: 600,
                            pointerEvents: 'none',
                        }}
                    >
                        <DragIndicatorIcon fontSize="small" sx={{ color: 'action.disabled' }} />
                        <DocumentNumberBox value={activeReq.numeration} />
                        <Box sx={{ flex: 1, overflow: 'hidden' }}>
                            <RequirementDescription description={activeReq.name} />
                        </Box>
                    </Paper>
                ) : activeGroup ? (
                    <Paper
                        elevation={6}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            padding: 1,
                            opacity: 0.95,
                            cursor: 'grabbing',
                            pointerEvents: 'none',
                            backgroundColor: '#f1f1f1',
                        }}
                    >
                        <DragIndicatorIcon fontSize="small" sx={{ color: 'action.disabled' }} />
                        <Typography variant="subtitle2">{`${activeGroup.numeration}. ${activeGroup.name}`}</Typography>
                    </Paper>
                ) : null}
            </DragOverlay>
        </DndContext>
    </>
}

// Wraps a MUI Tab so it can be reordered. The drag is initiated only from the
// handle icon (listeners + setActivatorNodeRef on the icon, not the Tab), so the
// rest of the tab keeps its normal click-to-select behaviour. We never spread
// `attributes` onto the Tab — that would set role="button" and break MUI's
// role="tab" semantics and arrow-key navigation.
const SortableTab = ({ typeId, editable, label, sx, ...tabProps }: { typeId: AppId; editable: boolean; label: React.ReactNode } & Record<string, any>) => {
    const { setNodeRef, setActivatorNodeRef, listeners, transform, transition, isDragging } = useSortable({
        id: typeId,
        disabled: !editable,
    })
    return <Tab
        ref={setNodeRef}
        {...tabProps}
        sx={{ px: 1, ...sx }}
        label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
                {editable && (
                    <Box
                        ref={setActivatorNodeRef}
                        {...listeners}
                        sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', touchAction: 'none' }}
                    >
                        <DragIndicatorIcon fontSize="small" sx={{ color: 'action.disabled', display: 'block' }} />
                    </Box>
                )}
                <Box sx={{ flex: 1, textAlign: 'left' }}>{label}</Box>
            </Box>
        }
        style={{
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0.4 : 1,
        }}
    />
}

interface RequirementsTabsProps {
    urs: Urs
    typeId: AppId
    setContextTypeId: (typeId: AppId) => void
    // Reorder wiring is optional — read-only consumers (e.g. the requirement
    // browser) omit it and tabs render without drag-and-drop.
    localTypes?: UrsRequirementType[]
    setLocalTypes?: Dispatch<SetStateAction<UrsRequirementType[]>>
    setIsDirty?: Dispatch<SetStateAction<boolean>>
    editable?: boolean
    // With a filter on, per-section match counts replace the unresolved-comment dot:
    // the dot answers a question the filter is already answering, and the count tells
    // the user which sections still hold something before they switch tabs.
    filterActive?: boolean
    matchCounts?: Map<AppId, number>
}
export const RequirementsTabs = ({ urs, typeId, setContextTypeId, localTypes, setLocalTypes, setIsDirty, editable = false, filterActive = false, matchCounts }: RequirementsTabsProps) => {
    const allRequirementTypes = localTypes ?? urs.types
    const handleReqsTabChange = useCallback((_, newReqTypeId) => {
        setContextTypeId(newReqTypeId)
    }, [])

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))
    const handleTabDragEnd = (e: DragEndEvent) => {
        const { active, over } = e
        if (!over || active.id === over.id) return
        if (!localTypes || !setLocalTypes || !setIsDirty) return
        const src = localTypes.findIndex(t => t.id === active.id)
        const tgt = localTypes.findIndex(t => t.id === over.id)
        if (src === -1 || tgt === -1 || src === tgt) return
        setLocalTypes(arrayMove(localTypes, src, tgt))
        setIsDirty(true)
    }

    const reqToTypeId = useMemo(() => {
        const map = new Map<AppId, AppId>()
        for (const type of urs.types) {
            for (const req of type.requirements) {
                map.set(req.id, type.id)
                for (const child of req.childRequirements) {
                    map.set(child.id, type.id)
                }
            }
            for (const group of type.groups) {
                for (const req of group.requirements) {
                    map.set(req.id, type.id)
                    for (const child of req.childRequirements) {
                        map.set(child.id, type.id)
                    }
                }
            }
        }
        return map
    }, [urs.types])

    const comments = useAppSelector(state => {
        if (selectURSComments(state).ursId === urs.id) {
            return selectAllURSComments(state)
        }
        return []
    })

    const typesWithUnresolved = useMemo(() => {
        const set = new Set<AppId>()
        for (const c of comments) {
            if (c.status === 'N' && c.parentCommentId === null && c.ursRequirementId !== null) {
                const tId = reqToTypeId.get(c.ursRequirementId)
                if (tId !== undefined) set.add(tId)
            }
        }
        return set
    }, [comments, reqToTypeId])

    return <div style={{ overflowY: 'auto' }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleTabDragEnd}>
            <SortableContext items={allRequirementTypes.map(t => t.id)} strategy={verticalListSortingStrategy}>
                <Tabs
                    orientation="vertical"
                    variant="standard"
                    value={typeId}
                    onChange={handleReqsTabChange}
                    sx={{
                        borderRight: 1,
                        borderColor: 'divider',
                    }}
                >
                    {allRequirementTypes.map(data => {
                        const matches = matchCounts?.get(data.id) ?? 0
                        return <SortableTab
                            key={data.id}
                            typeId={data.id}
                            editable={editable}
                            value={data.id}
                            sx={filterActive && matches === 0 ? { opacity: 0.4 } : undefined}
                            label={
                                <Badge
                                    variant={filterActive ? 'standard' : 'dot'}
                                    color={filterActive ? 'primary' : 'error'}
                                    badgeContent={filterActive ? matches : undefined}
                                    invisible={filterActive ? matches === 0 : !typesWithUnresolved.has(data.id)}
                                    sx={{
                                        [`& .MuiBadge-badge`]: {
                                            right: -6,
                                            top: 2,
                                        },
                                    }}
                                >
                                    {data.code || data.name}
                                </Badge>
                            }
                        />
                    })}
                </Tabs>
            </SortableContext>
        </DndContext>
    </div>
}
